import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { useLanguage } from "../language/LanguageContext";

function ChannelUpload({ emailUser }) {
  const { t } = useLanguage();
  const [excelFile, setExcelFile] = useState(null);
  const [uploaderEmail, setUploaderEmail] = useState("");
  const [manualChannel, setManualChannel] = useState({ languageCode: "", channelName: "", uploaderEmail: "" });
  const [channelDetails, setChannelDetails] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [myLanguageCounts, setMyLanguageCounts] = useState({});
  const [allLanguageCounts, setAllLanguageCounts] = useState({});
  const [isFetchingChannels, setIsFetchingChannels] = useState(false);
  const [activeTab, setActiveTab] = useState("myChannels");
  const [uploadTab, setUploadTab] = useState("excel");
  const [isEditing, setIsEditing] = useState(false);
  const [editChannel, setEditChannel] = useState({ channelId: "", languageCode: "", channelName: "", uploaderEmail: "" });
  
  // States for filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const excelFileInputRef = useRef();
  const ServerUrl = "https://voice-sample.tpvai.com/b/";
  const templateUrl = "https://voice-sample.tpvai.com/b/download/channel-template/channel_upload_template.xlsx";

  useEffect(() => {
    if (activeTab === "myChannels") {
      fetchChannelDetails();
    } else {
      fetchAllChannels();
    }
  }, [activeTab]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        Swal.fire("Error", t("Only .xlsx or .xls files are allowed."), "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Error", t("File size should be 5MB or less."), "error");
        return;
      }
      setExcelFile(file);
    }
  };

  const handleUploaderEmailChange = (e) => {
    setUploaderEmail(e.target.value);
  };

  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualChannel((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateLanguageCode = (languageCode) => {
    const languageCodeRegex = /^[a-zA-Z]{2}_[a-zA-Z]{2}$/;
    return languageCodeRegex.test(languageCode);
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      Swal.fire("Error", t("Please select an Excel file to upload."), "error");
      return;
    }
    if (!uploaderEmail || !validateEmail(uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }
    const formData = new FormData();
    formData.append("excelFile", excelFile);
    formData.append("email", emailUser);
    formData.append("uploaderEmail", uploaderEmail);
    try {
      const response = await axios.post(`${ServerUrl}upload_channels`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire({
        icon: "success",
        title: t("Success"),
        html: `
          <p>${t("Channels processed successfully!")}</p>
          <p>${t("Inserted Channels")}: ${response.data.count}</p>
          <p>${t("Skipped Duplicates")}: ${response.data.skippedCount || 0}</p>
          ${response.data.skippedChannels?.length > 0 ? `
            <p>${t("Skipped Channels")}: ${response.data.skippedChannels
              .map((ch) => `${ch.languageCode}: ${ch.channelName}`)
              .join(", ")}</p>
          ` : ""}
          ${response.data.invalidRows?.length > 0 ? `
            <p>${t("Invalid Rows")}: ${response.data.invalidRows
              .map((row) => `${row.languageCode}: ${row.channelName} (${row.error})`)
              .join(", ")}</p>
          ` : ""}
          <p>${t("Language Codes")}: ${Object.entries(response.data.languageCounts || {})
            .map(([lang, count]) => `${lang}: ${count}`)
            .join(", ")}</p>
        `,
      });
      setExcelFile(null);
      setUploaderEmail("");
      if (excelFileInputRef.current) excelFileInputRef.current.value = "";
      fetchChannelDetails();
      setActiveTab("myChannels");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to upload channels"), "error");
    }
  };

  const handleManualUpload = async () => {
    const { languageCode, channelName, uploaderEmail } = manualChannel;
    if (!languageCode || !channelName || !uploaderEmail) {
      Swal.fire("Error", t("Language code, channel name, and uploader email are required."), "error");
      return;
    }
    if (!validateEmail(uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }
    if (!validateLanguageCode(languageCode)) {
      Swal.fire("Error", t("Invalid language code format (expected: AA_BB, e.g., es_MX)."), "error");
      return;
    }
    try {
      const response = await axios.post(`${ServerUrl}upload_single_channel`, {
        email: emailUser,
        languageCode: languageCode.trim().toUpperCase(),
        channelName: channelName.trim(),
        uploaderEmail,
      });
      Swal.fire({
        icon: "success",
        title: t("Success"),
        html: `
          <p>${t("Channel processed successfully!")}</p>
          <p>${t("Inserted Channels")}: ${response.data.count}</p>
          ${response.data.skipped ? `
            <p>${t("Skipped Channel")}: ${response.data.skippedChannel.languageCode}: ${response.data.skippedChannel.channelName}</p>
          ` : ""}
          ${response.data.invalid ? `
            <p>${t("Invalid Channel")}: ${response.data.invalid.languageCode}: ${response.data.invalid.channelName} (${response.data.invalid.error})</p>
          ` : ""}
          <p>${t("Language Code")}: ${response.data.languageCounts[languageCode.toUpperCase()] || 0}</p>
        `,
      });
      setManualChannel({ languageCode: "", channelName: "", uploaderEmail: "" });
      fetchChannelDetails();
      setActiveTab("myChannels");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to upload channel"), "error");
    }
  };

  const fetchChannelDetails = async () => {
    setIsFetchingChannels(true);
    try {
      const response = await axios.get(`${ServerUrl}channels?email=${emailUser}`);
      if (response.status === 200) {
        setChannelDetails(response.data.data.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)));
        setMyLanguageCounts(response.data.languageCounts);
      } else {
        throw new Error(t("Failed to fetch channel details"));
      }
    } catch (error) {
      // Handle 404 silently for "No channels found" - just set empty state
      if (error.response?.status === 404) {
        setChannelDetails([]);
        setMyLanguageCounts({});
      } else {
        Swal.fire("Error", error.response?.data?.message || t("Failed to fetch channels"), "error");
        setMyLanguageCounts({});
      }
    } finally {
      setIsFetchingChannels(false);
    }
  };

  const fetchAllChannels = async () => {
    setIsFetchingChannels(true);
    try {
      const response = await axios.get(`${ServerUrl}all_channels`);
      if (response.status === 200) {
        setAllChannels(response.data.data.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)));
        setAllLanguageCounts(response.data.languageCounts);
      } else {
        throw new Error(t("Failed to fetch all channels"));
      }
    } catch (error) {
      // Handle 404 silently for "No channels found" - just set empty state
      if (error.response?.status === 404) {
        setAllChannels([]);
        setAllLanguageCounts({});
      } else {
        Swal.fire("Error", error.response?.data?.message || t("Failed to fetch all channels"), "error");
        setAllLanguageCounts({});
      }
    } finally {
      setIsFetchingChannels(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    const result = await Swal.fire({
      title: t("Are you sure?"),
      text: t("This channel will be deleted permanently."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Delete"),
      cancelButtonText: t("Cancel"),
    });
    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${ServerUrl}delete_channel`, {
          data: { channelId, email: emailUser },
        });
        if (response.status === 200) {
          Swal.fire("Success", t("Channel deleted successfully!"), "success");
          fetchChannelDetails();
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || t("Failed to delete channel"), "error");
      }
    }
  };

  const handleEditChannel = (channel) => {
    setEditChannel({
      channelId: channel._id,
      languageCode: channel.languageCode,
      channelName: channel.channelName,
      uploaderEmail: channel.uploaderEmail,
    });
    setIsEditing(true);
  };

  const handleUpdateChannel = async () => {
    if (!editChannel.languageCode || !editChannel.channelName || !editChannel.uploaderEmail) {
      Swal.fire("Error", t("Language code, channel name, and uploader email are required."), "error");
      return;
    }
    if (!validateEmail(editChannel.uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }
    if (!validateLanguageCode(editChannel.languageCode)) {
      Swal.fire("Error", t("Invalid language code format (expected: AA_BB, e.g., es_MX)."), "error");
      return;
    }
    try {
      const response = await axios.put(`${ServerUrl}update_channel`, {
        channelId: editChannel.channelId,
        email: emailUser,
        languageCode: editChannel.languageCode,
        channelName: editChannel.channelName,
        uploaderEmail: editChannel.uploaderEmail,
      });
      if (response.status === 200) {
        Swal.fire("Success", t("Channel updated successfully!"), "success");
        setIsEditing(false);
        fetchChannelDetails();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to update channel"), "error");
    }
  };

  // Updated filter function to search across all fields
  const filterChannels = (channels) => {
    return channels.filter((channel) => {
      const queryLower = searchQuery.toLowerCase();
      const matchesSearch = 
        channel.languageCode.toLowerCase().includes(queryLower) ||
        channel.channelName.toLowerCase().includes(queryLower) ||
        channel.uploaderEmail.toLowerCase().includes(queryLower) ||
        new Date(channel.uploadDate).toLocaleString().toLowerCase().includes(queryLower);
      const matchesLanguage = selectedLanguage ? channel.languageCode === selectedLanguage : true;
      return matchesSearch && matchesLanguage;
    });
  };

  // Get unique language codes for dropdown
  const getUniqueLanguages = (counts) => {
    return Object.keys(counts).sort();
  };

  const renderLanguageCounts = (counts) => (
    <div className="mt-4">
      <h4 className="text-md font-semibold text-gray-800 mb-2">{t("Channels by Language Code")}</h4>
      {Object.keys(counts).length > 0 ? (
        <table className="w-full text-left text-sm text-gray-700 border border-gray-200 rounded-lg">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2">{t("Language Code")}</th>
              <th className="px-4 py-2">{t("Channel Count")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(counts).map(([languageCode, count]) => (
              <tr key={languageCode} className="border-t">
                <td className="px-4 py-2">{languageCode}</td>
                <td className="px-4 py-2">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-500">{t("No language code counts available.")}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      <h2 className="text-xl font-semibold text-gray-800">{t("Upload Channel Names")}</h2>
      {/* Upload Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            uploadTab === "excel" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
          }`}
          onClick={() => setUploadTab("excel")}
        >
          {t("Excel Upload")}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            uploadTab === "manual" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
          }`}
          onClick={() => setUploadTab("manual")}
        >
          {t("Manual Upload")}
        </button>
      </div>
      {/* Upload Section */}
      {uploadTab === "excel" ? (
        <div className="space-y-4">
          <div>
            <label htmlFor="excel-file-input" className="block text-sm font-medium text-gray-700 mb-1">{t("Upload Excel File")}</label>
            <div className="flex items-center space-x-4 border border-gray-300 rounded-lg p-2">
              <button
                onClick={() => excelFileInputRef.current?.click()}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white text-xs sm:text-sm transition"
              >
                {t("Choose File")}
              </button>
              <span className="text-sm text-gray-600">
                {excelFile ? excelFile.name : t("No file chosen")}
              </span>
              <a
                href={templateUrl}
                download
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white text-xs sm:text-sm transition"
              >
                {t("Download Template")}
              </a>
              <input
                ref={excelFileInputRef}
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelFileChange}
                className="hidden"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{t("Excel file should contain 'languageCode' and 'channelName' columns.")}</p>
          </div>
          <div>
            <label htmlFor="uploader-email-input" className="block text-sm font-medium text-gray-700 mb-1">{t("Uploader Email")}</label>
            <input
              id="uploader-email-input"
              type="email"
              value={uploaderEmail}
              onChange={handleUploaderEmailChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter uploader email")}
            />
          </div>
          <button
            onClick={handleExcelUpload}
            className={`w-full sm:w-auto px-4 py-2 rounded text-white text-xs sm:text-sm ${
              !excelFile ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
            }`}
            disabled={!excelFile}
          >
            {t("Upload Channels")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="manual-language-code" className="block text-sm font-medium text-gray-700 mb-1">{t("Language Code")}</label>
            <input
              id="manual-language-code"
              name="languageCode"
              type="text"
              value={manualChannel.languageCode}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter language code (e.g., es_MX)")}
            />
          </div>
          <div>
            <label htmlFor="manual-channel-name" className="block text-sm font-medium text-gray-700 mb-1">{t("Channel Name")}</label>
            <input
              id="manual-channel-name"
              name="channelName"
              type="text"
              value={manualChannel.channelName}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter channel name")}
            />
          </div>
          <div>
            <label htmlFor="manual-uploader-email" className="block text-sm font-medium text-gray-700 mb-1">{t("Uploader Email")}</label>
            <input
              id="manual-uploader-email"
              name="uploaderEmail"
              type="email"
              value={manualChannel.uploaderEmail}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter uploader email")}
            />
          </div>
          <button
            onClick={handleManualUpload}
            className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-lg text-xs sm:text-sm hover:bg-green-600"
          >
            {t("Upload Channel")}
          </button>
        </div>
      )}
      {/* Channel List Tabs */}
      <div className="mt-6">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "myChannels" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
            onClick={() => setActiveTab("myChannels")}
          >
            {t("My Channels")}
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "allChannels" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
            onClick={() => setActiveTab("allChannels")}
          >
            {t("All Channels")}
          </button>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 my-4">
          {activeTab === "myChannels" ? t("My Uploaded Channels") : t("All Uploaded Channels")}
        </h3>

        {/* Filter UI */}
        <div className="flex space-x-4 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Search by language, name, email, or date")}
            className="border border-gray-300 rounded-lg p-2 text-sm flex-1"
          />
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="border border-gray-300 rounded-lg p-2 text-sm"
          >
            <option value="">{t("All Languages")}</option>
            {getUniqueLanguages(activeTab === "myChannels" ? myLanguageCounts : allLanguageCounts).map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

        {isFetchingChannels ? (
          <div className="flex justify-center items-center h-64">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
              {(() => {
                const channels = activeTab === "myChannels" ? channelDetails : allChannels;
                const filteredChannels = filterChannels(channels);
                return filteredChannels.length > 0 ? (
                  <table className="w-full text-left text-sm text-gray-700">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2">{t("Language Code")}</th>
                        <th className="px-4 py-2">{t("Channel Name")}</th>
                        <th className="px-4 py-2">{t("Uploader Email")}</th>
                        <th className="px-4 py-2">{t("Upload Date")}</th>
                        {activeTab === "myChannels" && <th className="px-4 py-2">{t("Actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChannels.map((channel) => (
                        <tr key={channel._id} className="border-t">
                          <td className="px-4 py-2">{channel.languageCode}</td>
                          <td className="px-4 py-2">{channel.channelName}</td>
                          <td className="px-4 py-2">{channel.uploaderEmail}</td>
                          <td className="px-4 py-2">{new Date(channel.uploadDate).toLocaleString()}</td>
                          {activeTab === "myChannels" && (
                            <td className="px-4 py-2 flex space-x-2">
                              <button
                                onClick={() => handleEditChannel(channel)}
                                className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm"
                                title={t("Edit Channel")}
                              >
                                {t("Edit")}
                              </button>
                              <button
                                onClick={() => handleDeleteChannel(channel._id)}
                                className="text-red-500 hover:text-red-700 text-xs sm:text-sm"
                                title={t("Delete Channel")}
                              >
                                {t("Delete")}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-500">{t("No channels match the filters.")}</p>
                );
              })()}
            </div> */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
  {(() => {
    const filteredChannels = filterChannels(allChannels); // Always use allChannels
    return filteredChannels.length > 0 ? (
      <table className="w-full text-left text-sm text-gray-700">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2">{t("Language Code")}</th>
            <th className="px-4 py-2">{t("Channel Name")}</th>
            <th className="px-4 py-2">{t("Uploader Email")}</th>
            <th className="px-4 py-2">{t("Upload Date")}</th>
            <th className="px-4 py-2">{t("Actions")}</th> {/* Always show Actions */}
          </tr>
        </thead>
        <tbody>
          {filteredChannels.map((channel) => (
            <tr key={channel._id} className="border-t">
              <td className="px-4 py-2">{channel.languageCode}</td>
              <td className="px-4 py-2">{channel.channelName}</td>
              <td className="px-4 py-2">{channel.uploaderEmail}</td>
              <td className="px-4 py-2">{new Date(channel.uploadDate).toLocaleString()}</td>
              <td className="px-4 py-2 flex space-x-2"> {/* Always show Actions */}
                <button
                  onClick={() => handleEditChannel(channel)}
                  className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm"
                  title={t("Edit Channel")}
                >
                  {t("Edit")}
                </button>
                <button
                  onClick={() => handleDeleteChannel(channel._id)}
                  className="text-red-500 hover:text-red-700 text-xs sm:text-sm"
                  title={t("Delete Channel")}
                >
                  {t("Delete")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="text-sm text-gray-500">{t("No channels match the filters.")}</p>
    );
  })()}
</div>

            {renderLanguageCounts(activeTab === "myChannels" ? myLanguageCounts : allLanguageCounts)}
          </div>
        )}
      </div>
      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("Edit Channel")}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("Language Code")}</label>
                <input
                  type="text"
                  value={editChannel.languageCode}
                  onChange={(e) => setEditChannel({ ...editChannel, languageCode: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter language code (e.g., es_MX)")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("Channel Name")}</label>
                <input
                  type="text"
                  value={editChannel.channelName}
                  onChange={(e) => setEditChannel({ ...editChannel, channelName: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter channel name")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("Uploader Email")}</label>
                <input
                  type="email"
                  value={editChannel.uploaderEmail}
                  onChange={(e) => setEditChannel({ ...editChannel, uploaderEmail: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter uploader email")}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg text-sm hover:bg-gray-400"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleUpdateChannel}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChannelUpload;